import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Activity, RefreshCw, AlertTriangle, ChevronDown, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';

type Verdict = 'ok' | 'warn' | 'crit' | 'unknown';
interface Metric { label: string; value: string; warn?: boolean }
interface Component { key: string; name: string; verdict: Verdict; detail?: string; metrics?: Metric[] }
interface QueueHealth {
  key: string; name: string; verdict: Verdict;
  pending: number; running: number; failed: number; done_total: number;
  stuck: number; oldest_pending: number; overdue: boolean;
  last_error?: string; can_reprocess: boolean; reason?: string;
}
interface Snapshot { verdict: Verdict; jobs: QueueHealth[]; infra: Component[] }
interface FailedJob { id: string; age: string; error?: string }

const DOT: Record<Verdict, string> = { ok: 'bg-emerald-500', warn: 'bg-amber-500', crit: 'bg-destructive', unknown: 'bg-muted-foreground' };
const LABEL: Record<Verdict, string> = { ok: 'OK', warn: 'Atenção', crit: 'Crítico', unknown: '—' };
const VAR: Record<Verdict, 'success' | 'warning' | 'destructive' | 'secondary'> = { ok: 'success', warn: 'warning', crit: 'destructive', unknown: 'secondary' };

function Dot({ v }: { v: Verdict }) {
  return <span className={`inline-block size-2.5 shrink-0 rounded-full ${DOT[v]}`} />;
}
const fmtAge = (s: string) => (s ? new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export function PlatformHealth() {
  const qc = useQueryClient();
  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['platform-health'],
    queryFn: async (): Promise<Snapshot | null> => {
      const r = await apiFetch('GET', '/platform/health');
      return r.ok ? (r.data as Snapshot) : null;
    },
    refetchInterval: 15000,
  });

  const [openQueue, setOpenQueue] = useState<string | null>(null);
  const failed = useQuery({
    queryKey: ['platform-health-failed', openQueue],
    enabled: !!openQueue,
    queryFn: async (): Promise<FailedJob[]> =>
      ((await apiFetch('GET', `/platform/health/queue/${openQueue}/failed`)).data as { data?: FailedJob[] })?.data ?? [],
  });

  const reprocess = async (key: string, id: string) => {
    const r = await apiFetch('POST', `/platform/health/queue/${key}/reprocess`, { id });
    if (!r.ok) { toast.error(((r.data as { message?: string })?.message) || 'Falha ao reprocessar.'); return; }
    toast.success('Job reenfileirado. Vai rodar no próximo ciclo.');
    qc.invalidateQueries({ queryKey: ['platform-health-failed', key] });
    qc.invalidateQueries({ queryKey: ['platform-health'] });
  };

  const overall = data?.verdict ?? 'unknown';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="size-5 text-primary" />
          <h2 className="text-lg font-semibold">Saúde da plataforma</h2>
          {data && <Badge variant={VAR[overall]} className="gap-1.5"><Dot v={overall} />{LABEL[overall]}</Badge>}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {dataUpdatedAt > 0 && <span>atualizado {new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')}</span>}
          <Button size="sm" variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ['platform-health'] })}>
            <RefreshCw className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-40 w-full" /> : !data ? (
        <Badge variant="secondary">Não foi possível carregar a saúde.</Badge>
      ) : (
        <>
          {/* Infraestrutura */}
          <section className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Infraestrutura</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 *:min-w-0">
              {data.infra.map((c) => (
                <Card key={c.key} className={c.verdict === 'crit' ? 'border-destructive/40' : ''}>
                  <CardContent className="flex flex-col gap-2 p-4">
                    <div className="flex items-center gap-2">
                      <Dot v={c.verdict} />
                      <span className="truncate text-sm font-medium">{c.name}</span>
                      <Badge variant={VAR[c.verdict]} className="ml-auto">{LABEL[c.verdict]}</Badge>
                    </div>
                    {c.detail && <p className="truncate text-xs text-muted-foreground">{c.detail}</p>}
                    {c.metrics && c.metrics.length > 0 && (
                      <div className="mt-1 flex flex-col gap-1">
                        {c.metrics.map((m, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{m.label}</span>
                            <span className={`font-medium tabular-nums ${m.warn ? 'text-amber-600' : ''}`}>{m.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <MetaWebhookCard />

          {/* Jobs */}
          <section className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Filas de jobs</p>
            <div className="grid gap-3 lg:grid-cols-2 *:min-w-0">
              {data.jobs.map((q) => (
                <Card key={q.key} className={q.verdict === 'crit' ? 'border-destructive/40' : ''}>
                  <CardHeader className="flex-row items-center gap-2 pb-2">
                    <Dot v={q.verdict} />
                    <CardTitle className="text-sm">{q.name}</CardTitle>
                    <Badge variant={VAR[q.verdict]} className="ml-auto">{LABEL[q.verdict]}</Badge>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <Stat label="Pendentes" value={q.pending} />
                      <Stat label="Rodando" value={q.running} warn={q.stuck > 0} />
                      <Stat label="Falhas" value={q.failed} warn={q.failed > 0} />
                      <Stat label="Concluídos" value={q.done_total} />
                    </div>
                    {q.reason && (
                      <p className="flex items-center gap-1.5 text-xs text-destructive">
                        <AlertTriangle className="size-3.5 shrink-0" /> {q.reason}
                      </p>
                    )}
                    {q.failed > 0 && q.can_reprocess && (
                      <div className="rounded-lg border">
                        <button
                          type="button"
                          onClick={() => setOpenQueue(openQueue === q.key ? null : q.key)}
                          className="flex w-full items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/40"
                        >
                          <span>Ver {q.failed} falha(s) e reprocessar</span>
                          <ChevronDown className={`size-4 transition-transform ${openQueue === q.key ? 'rotate-180' : ''}`} />
                        </button>
                        {openQueue === q.key && (
                          <div className="border-t">
                            {failed.isLoading ? (
                              <div className="p-3"><Skeleton className="h-8 w-full" /></div>
                            ) : (failed.data ?? []).length === 0 ? (
                              <p className="p-3 text-xs text-muted-foreground">Nenhuma falha.</p>
                            ) : (
                              <ul className="divide-y">
                                {(failed.data ?? []).map((f) => (
                                  <li key={f.id} className="flex items-center gap-2 px-3 py-2 text-xs">
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate font-mono text-[0.7rem] text-muted-foreground">{f.id}</p>
                                      {f.error && <p className="truncate text-destructive" title={f.error}>{f.error}</p>}
                                      <p className="text-muted-foreground">{fmtAge(f.age)}</p>
                                    </div>
                                    <Button size="sm" variant="outline" className="shrink-0" onClick={() => reprocess(q.key, f.id)}>
                                      <RotateCcw className="size-3.5" /> Reprocessar
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {q.failed > 0 && !q.can_reprocess && (
                      <p className="text-xs text-muted-foreground">Esta fila não reprocessa pela UI (ex.: faturas seguem o motor de cobrança).</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Atualiza sozinho a cada 15s. Quando algo fica crítico, vira demanda no Monitoramento do bFocus (uma por
            problema, com as ocorrências contadas) — não há alerta por e-mail. O status de cluster/deploy da DigitalOcean aparece quando o <code>DO_API_TOKEN</code> está configurado.
          </p>
        </>
      )}
    </div>
  );
}

interface MetaWebhook {
  enabled: boolean;
  status: {
    checked_at: string; expected_url: string; callback_url: string; active: boolean;
    fields?: string[]; missing?: string[]; ok: boolean; fixed?: string[]; error?: string;
    wabas: { waba_id: string; subscribed: boolean; fixed?: boolean; error?: string }[];
  };
}

// Webhook da Meta: para onde ela entrega os eventos. Já ficou apontado para um
// túnel de teste e a produção não recebeu nada sem ninguém perceber — aqui se
// vê, e se corrige, sem abrir o App Dashboard da Meta. A API também corrige
// sozinha a cada 30 minutos.
function MetaWebhookCard() {
  const qc = useQueryClient();
  const [fixing, setFixing] = useState(false);
  const { data } = useQuery({
    queryKey: ['platform-meta-webhook'],
    queryFn: async (): Promise<MetaWebhook | null> => {
      const r = await apiFetch('GET', '/platform/health/meta-webhook');
      return r.ok ? (r.data as MetaWebhook) : null;
    },
    refetchInterval: 60000,
  });
  if (!data) return null;
  const s = data.status;
  const v: Verdict = !data.enabled ? 'unknown' : s.ok ? 'ok' : 'crit';
  const fix = async () => {
    setFixing(true);
    const r = await apiFetch('POST', '/platform/health/meta-webhook/fix');
    setFixing(false);
    const st = (r.data as MetaWebhook | undefined)?.status;
    if (!r.ok || !st) { toast.error('Falha ao corrigir o webhook da Meta.'); return; }
    if (st.ok) toast.success('Webhook da Meta apontado para a produção e com todos os campos.');
    else toast.error(st.error || 'A Meta ainda não está entregando tudo: veja os detalhes.');
    qc.setQueryData(['platform-meta-webhook'], r.data);
  };
  return (
    <section className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">WhatsApp oficial</p>
      <Card className={v === 'crit' ? 'border-destructive/40' : ''}>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Dot v={v} />
            <span className="text-sm font-medium">Webhook da Meta</span>
            <Badge variant={VAR[v]}>{!data.enabled ? 'Desligado' : s.ok ? 'Recebendo' : 'Não recebe'}</Badge>
            <span className="ml-auto text-xs text-muted-foreground">conferido {fmtAge(s.checked_at)}</span>
          </div>
          {!data.enabled ? (
            <p className="text-sm text-muted-foreground">Sem META_WEBHOOK_URL neste ambiente: a conferência automática fica desligada (só a produção mexe no webhook do app).</p>
          ) : (
            <>
              <dl className="grid gap-1 text-sm sm:grid-cols-[10rem_1fr]">
                <dt className="text-muted-foreground">Entrega em</dt>
                <dd className={`break-all font-mono text-xs ${s.callback_url !== s.expected_url ? 'text-destructive' : ''}`}>{s.callback_url || '—'}</dd>
                <dt className="text-muted-foreground">Deveria ser</dt>
                <dd className="break-all font-mono text-xs">{s.expected_url}</dd>
                <dt className="text-muted-foreground">Campos faltando</dt>
                <dd className="font-mono text-xs">{s.missing?.length ? s.missing.join(', ') : 'nenhum'}</dd>
                <dt className="text-muted-foreground">Contas inscritas</dt>
                <dd className="text-xs">
                  {s.wabas.length === 0 ? '—' : s.wabas.map((w) => (
                    <span key={w.waba_id} className={`mr-3 font-mono ${w.subscribed ? '' : 'text-destructive'}`}>
                      {w.waba_id} {w.subscribed ? '✓' : '✗'}{w.fixed ? ' (reinscrita)' : ''}
                    </span>
                  ))}
                </dd>
              </dl>
              {s.error && <p className="flex items-start gap-1.5 text-xs text-destructive"><AlertTriangle className="mt-px size-3.5 shrink-0" />{s.error}</p>}
              {!!s.fixed?.length && <p className="text-xs text-muted-foreground">Corrigido na última conferência: {s.fixed.join(', ')}</p>}
              <div>
                <Button size="sm" variant={s.ok ? 'outline' : 'default'} onClick={fix} disabled={fixing}>
                  <RotateCcw className={`size-3.5 ${fixing ? 'animate-spin' : ''}`} /> {s.ok ? 'Conferir de novo' : 'Corrigir agora'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function Stat({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="rounded-md border bg-muted/20 p-2">
      <p className={`text-lg font-bold tabular-nums ${warn ? 'text-destructive' : ''}`}>{value}</p>
      <p className="text-[0.65rem] text-muted-foreground">{label}</p>
    </div>
  );
}
